import React from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import { truncate } from "lodash";

import { Select } from "@/Components/v2/atoms/Select";
import { Stack } from "@/Components/v2/atoms/Stack";
import { Typography } from "@/Components/v2/atoms/Typography";
import { ScenarioOption, ActionPlanFormData } from "./types";
import { FormControl } from "@/Components/v2/atoms/FormControl";
import { MenuItem } from "@/Components/v2/atoms/MenuItem";
import { FormHelperText, Skeleton } from "@mui/material";

type ScenarioSelectionFieldProps = {
  control: Control<ActionPlanFormData>;
  errors: FieldErrors<ActionPlanFormData>;
  scenarioOptions: ScenarioOption[];
  scenarioLabel: string;
  isLoading: boolean;
};

export const ScenarioSelectionField: React.FC<ScenarioSelectionFieldProps> = ({
  control,
  errors,
  scenarioOptions,
  scenarioLabel,
  isLoading,
}) => {
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2" fontWeight={600}>
        Base Scenario
      </Typography>
      {isLoading ? (
        <Skeleton variant="rectangular" height={40} />
      ) : (
        <Controller
          name="scenarioId"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.scenarioId}>
              <Select
                {...field}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 350,
                    },
                  },
                }}
                sx={{
                  "& .MuiSelect-select": {
                    color: field.value ? "text.primary" : "grey.500",
                  },
                }}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: "text.primary", fontSize: "14px" }}>
                        Select a base scenario
                      </Typography>
                    );
                  }
                  return truncate(scenarioLabel, { length: 48 });
                }}
              >
                {scenarioOptions.map((option) => (
                  <MenuItem key={option.uuid} value={option.uuid}>
                    {truncate(option.name, { length: 48 })}
                  </MenuItem>
                ))}
              </Select>
              {errors.scenarioId && <FormHelperText>{errors.scenarioId.message}</FormHelperText>}
            </FormControl>
          )}
        />
      )}
      <Typography variant="caption" color="text.secondary">
        Base Scenario defines the business unit, end date and geography of the action plan. This
        cannot be changed.
      </Typography>
    </Stack>
  );
};
